import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sliders, ArrowUpDown, Bell, Search } from 'lucide-react'
import FilterModal from '../../components/FilterModal'
import NotificationsModal from '../../components/NotificationsModal'
import SortViewModal from '../../components/SortViewModal'
import { getRegionLabel } from '../../helpers/propertyTypeHelpers'

const DashboardHeader = ({
  user,
  districts,
  selectedType,
  onTypeChange,
  selectedRegion,
  onRegionChange,
  selectedDistricts,
  onDistrictsChange,
  selectedMunicipalities,
  onMunicipalitiesChange,
  selectedSort,
  onSortChange,
  view,
  onViewChange,
  originalData,
  hideNoPrice,
  onHideNoPriceChange,
  selectedDisplayOptions,
  onDisplayOptionsChange,
  selectedOfferType,
  selectedDateRange,
  onDateRangeChange,
  minPrice,
  maxPrice,
  onPriceChange,
  selectedLayouts,
  onLayoutChange,
  searchInput,
  onSearchInputChange,
}) => {
  const navigate = useNavigate()
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [isSortViewModalOpen, setIsSortViewModalOpen] = useState(false)
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false)

  // Calculate active filters count
  const activeFiltersCount = [
    selectedDistricts?.length > 0,
    selectedMunicipalities?.length > 0,
    selectedLayouts?.length > 0,
    selectedDisplayOptions?.length > 0,
    selectedDateRange?.start || selectedDateRange?.end,
    minPrice > 0 || maxPrice < 30000000,
  ].filter(Boolean).length

  const getPropertyTypeLabel = () => {
    switch (selectedType) {
      case 'byt':
        return 'Byty'
      case 'chata':
        return 'Chaty'
      case 'dum':
        return 'Domy'
      case 'pozemek':
        return 'Pozemky'
      case 'komercni':
        return 'Komerční'
      case 'ostatni':
        return 'Ostatní'
      default:
        return selectedType
    }
  }

  return (
    <>
      <div className="w-full bg-white h-10">
        <div className="flex items-center justify-between">
          {/* Title - responsive text */}
          <div className="flex-1 min-w-0">
            <div className="text-sm sm:text-lg font-medium truncate">
              {getPropertyTypeLabel()} · {getRegionLabel(selectedRegion)}
            </div>
          </div>

          {/* Search and controls */}
          <div className="flex shrink-0 items-center gap-1 sm:gap-3 ml-2">
            <div className="relative hidden w-64 lg:block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={searchInput}
                onChange={(event) => onSearchInputChange(event.target.value)}
                placeholder="Ulice nebo čtvrť…"
                className="w-full rounded-lg border border-gray-300 py-1.5 pl-9 pr-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
              />
            </div>
            <button
              onClick={() => setIsSortViewModalOpen(true)}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-xs sm:text-sm"
            >
              <ArrowUpDown size={16} className="sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Řazení</span>
              {selectedSort !== 'date-desc' && <span className="ml-1 px-1.5 sm:px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">1</span>}
            </button>
            <button
              onClick={() => setIsFilterModalOpen(true)}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-xs sm:text-sm"
            >
              <Sliders size={16} className="sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Filtry</span>
              {activeFiltersCount > 0 && <span className="ml-1 px-1.5 sm:px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">{activeFiltersCount}</span>}
            </button>
          </div>

          {/* Right side - user info and actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* <span className="hidden sm:inline text-sm text-gray-600 mr-2">{user?.username}</span>
              <button onClick={() => navigate('/valuations')} className="p-1.5 sm:p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md" title="Valuations">
                <Calculator size={16} className="sm:w-5 sm:h-5" />
              </button>
              <button onClick={() => navigate('/listings')} className="p-1.5 sm:p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md" title="Agent Listings">
                <Home size={16} className="sm:w-5 sm:h-5" />
              </button> */}
            <button onClick={() => setIsNotificationsModalOpen(true)} className="p-1.5 sm:p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md" title="Notifikace">
              <Bell size={16} className="sm:w-5 sm:h-5" />
            </button>
            {/* <button onClick={logout} className="p-1.5 sm:p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md" title="Odhlásit">
                <LogOut size={16} className="sm:w-5 sm:h-5" />
              </button> */}
          </div>
        </div>
      </div>

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        activeFiltersCount={activeFiltersCount}
        // Type and region
        selectedType={selectedType}
        onTypeChange={onTypeChange}
        selectedRegion={selectedRegion}
        onRegionChange={onRegionChange}
        selectedOfferType={selectedOfferType}
        // Districts
        districts={districts}
        selectedDistricts={selectedDistricts}
        onDistrictsChange={onDistrictsChange}
        selectedMunicipalities={selectedMunicipalities}
        onMunicipalitiesChange={onMunicipalitiesChange}
        // Layouts
        selectedLayouts={selectedLayouts}
        onLayoutChange={onLayoutChange}
        // Display options
        selectedDisplayOptions={selectedDisplayOptions}
        onDisplayOptionsChange={onDisplayOptionsChange}
        // Date range
        selectedDateRange={selectedDateRange}
        onDateRangeChange={onDateRangeChange}
        // Price range
        minPrice={minPrice}
        maxPrice={maxPrice}
        onPriceChange={onPriceChange}
        // Navigation
        navigate={navigate}
      />

      <SortViewModal
        isOpen={isSortViewModalOpen}
        onClose={() => setIsSortViewModalOpen(false)}
        view={view}
        onViewChange={onViewChange}
        selectedSort={selectedSort}
        onSortChange={onSortChange}
      />

      <NotificationsModal
        isOpen={isNotificationsModalOpen}
        onClose={() => setIsNotificationsModalOpen(false)}
        onApplyAlert={(alert) => {
          // Navigate to the correct URL based on alert conditions
          const alertOfferType = alert.offerType
          const alertType = alert.type
          const alertRegion = alert.region

          // Build query parameters based on alert conditions
          const queryParams = new URLSearchParams()

          // Add districts if specified
          if (alert.districts && alert.districts.length > 0) {
            queryParams.set('districts', alert.districts.join(','))
          }

          // Add layouts if specified
          if (alert.layouts && alert.layouts.length > 0) {
            // Convert UI format (2+kk) to URL format (2kk)
            const urlLayouts = alert.layouts.map((layout) => {
              const match = layout.match(/^(\d)\+(kk|\d)$/i)
              if (match) {
                return `${match[1]}${match[2]}`
              }
              return layout
            })
            queryParams.set('layouts', urlLayouts.join(','))
          }

          // Add price per m² range if specified
          if (alert.conditions.priceMin) {
            queryParams.set('minPricePerM2', alert.conditions.priceMin.toString())
          }
          if (alert.conditions.priceMax) {
            queryParams.set('maxPricePerM2', alert.conditions.priceMax.toString())
          }

          // Add stavba conditions as display options
          if (alert.conditions.stavba && alert.conditions.stavba.length > 0) {
            queryParams.set('options', alert.conditions.stavba.join(','))
          }

          const queryString = queryParams.toString()
          const url = `/${alertOfferType}/${alertType}/${alertRegion}${queryString ? `?${queryString}` : ''}`

          navigate(url)
        }}
      />
    </>
  )
}

export default DashboardHeader
